/* ==========================================================================
   PAWEVER – CART DRAWER (pw-cart.js) · pw-cart v12
   - Line quantity / remove, Subscribe & Save switch, shipping-bar nudges
   - Upsell carousel (dots, swipe, auto-rotate while open) and one-time Add to cart
   - Discount codes (cart/update.js, invalid codes are removed again with a message)
   - Re-renders every [data-pw-region] after any cart change (ours or the theme's)
   - Hides Shopify Inbox while the drawer is open
   Layout (full-bleed blocks, pinned footer) is pure CSS – see pw-cart.css.
   ========================================================================== */
(() => {
  if (window.__pwCart) return;
  window.__pwCart = true;

  const VERSION = '12';
  // The drawer lives in the overlay section group, so its real section id is
  // "sections--<group>__cart-drawer". Rendering the bare "cart-drawer" id returns the section
  // with DEFAULT settings and no blocks (no upsell slides), which caused a flash on every update.
  const sectionId = () => {
    const wrapper = document.querySelector('[id^="shopify-section-"][id$="__cart-drawer"], #shopify-section-cart-drawer');
    return wrapper ? wrapper.id.replace('shopify-section-', '') : 'cart-drawer';
  };
  const CHAT_SELECTORS = '#shopify-chat, #ShopifyChat, inbox-online-store-chat, #dummy-chat-button-iframe';

  let selfUpdateUntil = 0;
  let refreshTimer;
  let carouselStartIndex = 0;

  const getDrawer = () => document.getElementById('cart-drawer');
  const routeRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const toInt = (value) => parseInt(value ?? '', 10);
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Cart API ---------- */

  async function cartPost(path, payload) {
    const response = await fetch(`${routeRoot()}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...payload, sections: [sectionId()], sections_url: window.location.pathname })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || 'Cart update failed');
    return data;
  }

  async function refresh() {
    try {
      const response = await fetch(`${window.location.pathname}?sections=${encodeURIComponent(sectionId())}`, {
        headers: { Accept: 'application/json' }
      });
      if (response.ok) render(await response.json());
    } catch (error) {
      console.error('[pw-cart] refresh failed', error);
    }
  }

  /* ---------- Rendering ---------- */

  function snapshot(drawer) {
    const fill = drawer.querySelector('.pw-ship__fill');
    const input = drawer.querySelector('[data-pw-discount-input]');
    const carousel = drawer.querySelector('[data-pw-carousel]');
    return {
      progress: fill ? fill.dataset.progress : null,
      discountDraft: input ? input.value : '',
      discountFocused: input ? document.activeElement === input : false,
      carouselIndex: carousel ? carousel.__index || 0 : 0
    };
  }

  function restore(drawer, state) {
    // Animate the shipping bar only when the progress actually changes
    const fill = drawer.querySelector('.pw-ship__fill');
    if (fill && state.progress !== null && state.progress !== fill.dataset.progress) {
      const target = fill.dataset.progress;
      fill.style.setProperty('--pw-progress', `${state.progress}%`);
      void fill.offsetWidth;
      requestAnimationFrame(() => fill.style.setProperty('--pw-progress', `${target}%`));
    }

    const input = drawer.querySelector('[data-pw-discount-input]');
    if (input && state.discountDraft) input.value = state.discountDraft;
    if (input && state.discountFocused) input.focus();

    carouselStartIndex = state.carouselIndex;
  }

  function render(sections) {
    const html = sections && sections[sectionId()];
    const drawer = getDrawer();
    if (!html || !drawer) return;

    const next = new DOMParser().parseFromString(html, 'text/html').getElementById('cart-drawer');
    if (!next) return;

    const state = snapshot(drawer);
    const incoming = [...next.querySelectorAll('[data-pw-region]')];
    const sameShape =
      incoming.length > 0 &&
      incoming.length === drawer.querySelectorAll('[data-pw-region]').length &&
      incoming.every((el) => drawer.querySelector(`[data-pw-region="${el.dataset.pwRegion}"]`));

    if (sameShape) {
      incoming.forEach((el) => {
        drawer.querySelector(`[data-pw-region="${el.dataset.pwRegion}"]`).replaceWith(document.importNode(el, true));
      });
    } else {
      // Structure changed (e.g. cart emptied) – replace the drawer's contents wholesale
      drawer.replaceChildren(...[...next.childNodes].map((node) => document.importNode(node, true)));
      drawer.classList.toggle('drawer--center-body', next.classList.contains('drawer--center-body'));
    }

    restore(drawer, state);
    initCarousels();
  }

  /* ---------- Upsell carousel ---------- */

  function goTo(carousel, index) {
    const track = carousel.querySelector('[data-pw-track]');
    if (!track) return;
    track.scrollTo({ left: index * track.clientWidth, behavior: reducedMotion() ? 'auto' : 'smooth' });
    carousel.__index = index;
    carousel.__last = Date.now();
  }

  function initCarousels() {
    document.querySelectorAll('#cart-drawer [data-pw-carousel]').forEach((carousel) => {
      if (carousel.__pwInit) return;
      carousel.__pwInit = true;

      const track = carousel.querySelector('[data-pw-track]');
      const dots = [...carousel.querySelectorAll('[data-pw-dot]')];
      if (!track) return;

      const setCurrent = (index) => {
        carousel.__index = index;
        dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
      };

      // Keep the slide the shopper was on after a re-render
      const start = Math.min(carouselStartIndex, Math.max(dots.length - 1, 0));
      carouselStartIndex = 0;
      if (start > 0) track.scrollLeft = start * track.clientWidth;
      setCurrent(start);
      carousel.__last = Date.now();

      let frame;
      track.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => setCurrent(Math.round(track.scrollLeft / (track.clientWidth || 1))));
        },
        { passive: true }
      );

      dots.forEach((dot, i) =>
        dot.addEventListener('click', () => {
          goTo(carousel, i);
          carousel.__pausedUntil = Date.now() + 10000;
        })
      );

      const pause = () => {
        carousel.__pausedUntil = Date.now() + 8000;
      };
      ['pointerdown', 'touchstart', 'wheel', 'focusin'].forEach((name) =>
        carousel.addEventListener(name, pause, { passive: true })
      );
      carousel.addEventListener('mouseenter', () => {
        carousel.__hover = true;
      });
      carousel.addEventListener('mouseleave', () => {
        carousel.__hover = false;
        carousel.__last = Date.now();
      });
    });
  }

  // Auto-rotate: only while the drawer is open and the tab is visible
  setInterval(() => {
    initCarousels(); // picks up carousels the theme re-rendered on its own (no-op otherwise)
    const drawer = getDrawer();
    const open = Boolean(drawer) && !document.hidden && isDrawerOpen(drawer);
    const now = Date.now();

    document.querySelectorAll('#cart-drawer [data-pw-carousel]').forEach((carousel) => {
      const seconds = toInt(carousel.dataset.autoplay) || 0;
      const count = carousel.querySelectorAll('[data-pw-dot]').length;
      if (!open || !seconds || count < 2 || carousel.__hover || now < (carousel.__pausedUntil || 0)) {
        carousel.__last = now; // restart the countdown once it can rotate again
        return;
      }
      if (now - (carousel.__last || now) >= seconds * 1000) {
        goTo(carousel, ((carousel.__index || 0) + 1) % count);
      }
    });
  }, 500);

  /* ---------- Actions ---------- */

  async function run(trigger, task) {
    const drawer = getDrawer();
    const busyScope = trigger?.closest('.pw-line, .pw-subcard, .pw-upsell, .pw-discount');
    drawer?.setAttribute('aria-busy', 'true');
    busyScope?.classList.add('is-loading');
    if (trigger) trigger.disabled = true;

    try {
      const data = await task();
      selfUpdateUntil = Date.now() + 1500;
      render(data.sections);
      // Lets the theme update anything else that shows the cart (e.g. the header count)
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      return true;
    } catch (error) {
      console.error('[pw-cart]', error);
      return false;
    } finally {
      drawer?.removeAttribute('aria-busy');
      if (busyScope?.isConnected) busyScope.classList.remove('is-loading');
      if (trigger?.isConnected) trigger.disabled = false;
    }
  }

  const changeLine = (key, quantity, extra = {}) => cartPost('cart/change.js', { id: key, quantity, ...extra });

  /* ---------- Discount codes ---------- */

  const appliedCodes = () =>
    (document.querySelector('[data-pw-applied-codes]')?.dataset.pwAppliedCodes || '')
      .split(',')
      .map((code) => code.trim())
      .filter(Boolean);

  function showDiscountError(message) {
    const el = document.querySelector('[data-pw-discount-error]');
    if (!el) return;
    el.textContent = message;
    el.hidden = !message;
  }

  async function applyCode(trigger) {
    const code = (document.querySelector('[data-pw-discount-input]')?.value || '').trim();
    if (!code) return;

    const existing = appliedCodes();
    if (existing.some((c) => c.toLowerCase() === code.toLowerCase())) {
      showDiscountError(`${code} is already applied.`);
      return;
    }

    let invalid = false;
    const ok = await run(trigger, async () => {
      let data = await cartPost('cart/update.js', { discount: [...existing, code].join(',') });
      const entry = (data.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());
      if (!entry || !entry.applicable) {
        invalid = true;
        data = await cartPost('cart/update.js', { discount: existing.join(',') });
      }
      return data;
    });

    const field = document.querySelector('[data-pw-discount-input]');
    if (!ok) {
      showDiscountError('Something went wrong. Please try again.');
    } else if (invalid) {
      if (field) field.value = code;
      showDiscountError(`${code} isn't valid for the items in your cart.`);
    } else if (field) {
      field.value = '';
    }
  }

  function removeCode(trigger, code) {
    const remaining = appliedCodes().filter((c) => c.toLowerCase() !== code.toLowerCase());
    run(trigger, () => cartPost('cart/update.js', { discount: remaining.join(',') }));
  }

  /* ---------- Event wiring ---------- */

  // Subscribe & Save switch on a one-time line
  document.addEventListener('change', async (event) => {
    const checkbox = event.target.closest?.('.cart-subscription-upsell__checkbox');
    if (!checkbox || !checkbox.checked) return;

    const card = checkbox.closest('.cart-subscription-upsell');
    const key = card?.dataset.lineKey;
    const plan = toInt(card?.dataset.sellingPlanId);
    if (!key || !plan) return;

    const ok = await run(checkbox, () => changeLine(key, toInt(card.dataset.quantity) || 1, { selling_plan: plan }));
    if (!ok) checkbox.checked = false;
  });

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!target.closest?.('#cart-drawer')) return;

    // Quantity +/- and remove (quantity 0)
    const qty = target.closest('[data-pw-qty]');
    if (qty) {
      event.preventDefault();
      if (qty.dataset.lineKey) run(qty, () => changeLine(qty.dataset.lineKey, Math.max(0, toInt(qty.dataset.quantity) || 0)));
      return;
    }

    // "Or subscribe & ship free" (optional, off by default) – same plan as the line's own switch
    const subscribe = target.closest('[data-pw-subscribe]');
    if (subscribe) {
      event.preventDefault();
      const key = subscribe.dataset.lineKey;
      const card = key && document.querySelector(`.cart-subscription-upsell[data-line-key="${CSS.escape(key)}"]`);
      const plan = toInt(card?.dataset.sellingPlanId) || toInt(subscribe.dataset.sellingPlanId);
      if (key && plan) run(subscribe, () => changeLine(key, toInt(subscribe.dataset.quantity) || 1, { selling_plan: plan }));
      return;
    }

    // "Add a 2nd bottle"
    const bump = target.closest('[data-pw-qty-bump]');
    if (bump) {
      event.preventDefault();
      if (bump.dataset.lineKey) run(bump, () => changeLine(bump.dataset.lineKey, (toInt(bump.dataset.quantity) || 1) + 1));
      return;
    }

    // Upsell Add to cart (one-time)
    const add = target.closest('[data-pw-add]');
    if (add) {
      event.preventDefault();
      const id = toInt(add.dataset.variantId);
      if (id) run(add, () => cartPost('cart/add.js', { items: [{ id, quantity: 1 }] }));
      return;
    }

    const apply = target.closest('[data-pw-discount-apply]');
    if (apply) {
      event.preventDefault();
      showDiscountError('');
      applyCode(apply);
      return;
    }

    const remove = target.closest('[data-pw-remove-code]');
    if (remove) {
      event.preventDefault();
      removeCode(remove, remove.dataset.pwRemoveCode);
    }
  });

  // Enter in the discount field applies the code instead of submitting checkout
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' || !event.target.closest?.('[data-pw-discount-input]')) return;
    event.preventDefault();
    showDiscountError('');
    applyCode(document.querySelector('[data-pw-discount-apply]'));
  });

  // Theme-driven changes (add from a product page, etc.) – re-sync our regions
  const onExternalChange = () => {
    if (Date.now() < selfUpdateUntil) return;
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refresh, 150);
  };
  ['cart:change', 'cart:updated'].forEach((name) => {
    document.addEventListener(name, onExternalChange);
    document.documentElement.addEventListener(name, onExternalChange);
  });

  /* ---------- Hide Shopify Inbox while the drawer is open ---------- */

  function isDrawerOpen(drawer) {
    if (drawer.hasAttribute('open')) return true;
    if (drawer.getAttribute('aria-hidden') === 'true') return false;
    const probe = drawer.querySelector('[slot="header"]') || drawer;
    if (typeof probe.checkVisibility === 'function' &&
        !probe.checkVisibility({ visibilityProperty: true, opacityProperty: true })) {
      return false;
    }
    const rect = probe.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.left < window.innerWidth - 1 && rect.right > 1;
  }

  let syncQueued = false;
  function syncOpenState() {
    if (syncQueued) return;
    syncQueued = true;
    requestAnimationFrame(() => {
      syncQueued = false;
      const drawer = getDrawer();
      const open = drawer ? isDrawerOpen(drawer) : false;
      document.documentElement.classList.toggle('pw-cart-open', open);
      document.querySelectorAll(CHAT_SELECTORS).forEach((el) => {
        if (open) el.style.setProperty('display', 'none', 'important');
        else el.style.removeProperty('display');
      });
    });
  }

  function init() {
    const drawer = getDrawer();
    if (!drawer) return;

    const sectionVersion = drawer.dataset.pwVersion || 'none';
    if (sectionVersion !== VERSION) {
      console.warn(`[pw-cart] Version mismatch – pw-cart.js v${VERSION}, cart-drawer.liquid v${sectionVersion}.`);
    }

    const observer = new MutationObserver(syncOpenState);
    observer.observe(drawer, { attributes: true });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'style'] });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class', 'style'] });
    ['transitionend', 'animationend'].forEach((name) => drawer.addEventListener(name, syncOpenState, true));
    document.addEventListener('click', () => {
      setTimeout(syncOpenState, 60);
      setTimeout(syncOpenState, 500);
    });
    document.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') setTimeout(syncOpenState, 500);
    });

    initCarousels();
    syncOpenState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();